import React, { useEffect, useMemo, useState } from 'react';
import { authorizedFetch } from '../utils/api';

const LIMIT = 45;
const THRESHOLD_OPTIONS = [6, 7, 8, 9, 9.5];

function RecommendationCard({ look }) {
  const imageUrl = look.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-100 dark:border-gray-700">
      <div className="w-full aspect-[2/3] bg-gray-100 dark:bg-gray-700">
        <img
          src={imageUrl}
          alt={`${look.brand || 'Unknown'} look ${look.index ?? '-'}`}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
          }}
        />
      </div>

      <div className="p-4 space-y-1">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Look {look.index ?? '-'}</h3>
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
            Machine {typeof look.machine_score === 'number' ? look.machine_score.toFixed(2) : '-'}
          </span>
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300">{look.brand || 'Unknown Brand'}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{look.designer || 'Unknown Designer'}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{look.season || 'Unknown Season'} {look.year || ''}</div>
      </div>
    </div>
  );
}

export default function Recommendations() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [threshold, setThreshold] = useState(9.5);
  const [hasMore, setHasMore] = useState(false);

  const title = useMemo(() => `Machine Recommendations (>= ${threshold})`, [threshold]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        min_machine_score: threshold.toString(),
        page: page.toString(),
        limit: LIMIT.toString(),
        sort_order: 'desc'
      });

      const res = await authorizedFetch(`/fashion-looks/looks/recommendations?${params.toString()}`);
      if (!res.ok) {
        throw new Error('Failed to fetch recommendation looks');
      }

      const data = await res.json();
      setLooks(Array.isArray(data) ? data : []);
      setHasMore(Array.isArray(data) && data.length === LIMIT);
    } catch (err) {
      setError(err.message || 'Failed to fetch recommendation looks');
      setLooks([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [page, threshold]);

  return (
    <div className="p-4 animate-fade-in">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recommendation</h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Show fashion looks where machine score is greater than or equal to your threshold.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 flex flex-wrap items-center gap-3">
        <label htmlFor="machine-threshold" className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Machine score threshold
        </label>
        <select
          id="machine-threshold"
          value={threshold}
          onChange={(e) => {
            setThreshold(Number(e.target.value));
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {THRESHOLD_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {`>= ${value}`}
            </option>
          ))}
        </select>

        <button
          onClick={fetchRecommendations}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>

        <span className="text-sm text-gray-600 dark:text-gray-300">
          {title}: {looks.length} on current page
        </span>
      </div>

      {loading && (
        <div className="p-8 text-center text-gray-600 dark:text-gray-300">Loading recommendations...</div>
      )}

      {!loading && error && (
        <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
          {error}
        </div>
      )}

      {!loading && !error && looks.length === 0 && (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow">
          No looks found for this threshold.
        </div>
      )}

      {!loading && !error && looks.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {looks.map((look) => (
              <RecommendationCard key={look.id} look={look} />
            ))}
          </div>

          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 rounded bg-gray-200 text-gray-800 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100"
            >
              Prev
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-200">Page {page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore || loading}
              className="px-4 py-2 rounded bg-gray-200 text-gray-800 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
